import { styled, Typography, Skeleton, Chip } from '@mui/material';
import NumberFlow from '@number-flow/react';
import { Price as PriceType } from '@/schemas/sceneDataSchema';
import { PricingTerm } from '@/constants/pricingTypes';
import { useQuote, useShowLoader } from '@/hooks/use-quote';

export const Price = ({ component }: { component: PriceType }) => {
  const quote = useQuote();
  const showLoader = useShowLoader();

  if (!quote) {
    return (
      <Typography
        variant='body1'
        color='textSecondary'
      >
        Loading price...
      </Typography>
    );
  }

  const { initial_term_selection, monthly_in_cents, full_term_in_cents, price_delta_amount } = quote.details;
  const priceInCents = initial_term_selection === PricingTerm.MONTHLY ? monthly_in_cents : full_term_in_cents;

  // infer delta price - amount is increasing (positive) or decreasing (negative)
  const priceDelta = price_delta_amount ? parseFloat(price_delta_amount) : null;
  const priceDeltaDirection = priceDelta && priceDelta >= 0 ? 'increase' : 'decrease';
  const priceDeltaDisplay = priceDelta ? Math.abs(priceDelta).toFixed(2) : null;

  if (showLoader) {
    return (
      <Skeleton
        variant='rounded'
        width='100%'
        height={54}
        animation='wave'
      />
    );
  }

  if (priceInCents === 0) {
    return <StyledPrice variant={component.variant ?? 'h1'}>$--.--</StyledPrice>;
  }

  return (
    <>
      <StyledPrice variant={component.variant ?? 'h1'}>
        <NumberFlow
          value={priceInCents / 100}
          format={{ style: 'currency', currency: 'USD' }}
          suffix={initial_term_selection === PricingTerm.MONTHLY ? '/mo' : '/6 mo'}
          spinTiming={{
            duration: 750,
            easing: 'ease-in-out',
          }}
          opacityTiming={{
            duration: 350,
            easing: 'ease-in-out',
          }}
          willChange={true}
        />
      </StyledPrice>
      {priceDeltaDisplay && (
        <Chip
          label={`${priceDeltaDirection === 'increase' ? '+' : '-'} $${priceDeltaDisplay} premium ${priceDeltaDirection}`}
          variant='outlined'
          color='info'
          size='small'
          sx={{ alignSelf: 'start' }}
        />
      )}
    </>
  );
};

const variantsWithSmallSuffix = ['h1', 'h2', 'h3', 'h4'];

const StyledPrice = styled(Typography)<{ variant: string }>`
  ${({ variant }) =>
    variantsWithSmallSuffix.includes(variant) &&
    `
      & number-flow-react::part(suffix) {
        font-size: 0.6em;
      }
    `}
`;
